/**
 * The organization's single sign-on health, as the daily check wrote it on
 * the organization document (which every member may read): whether the
 * Entra secret is about to expire, or has.
 */

import { useQuery } from '@tanstack/react-query';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { app } from '../../lib/firebase';

export interface SsoHealth {
  status: 'ok' | 'expiring' | 'expired' | 'unknown';
  daysLeft: number | null;
  secretExpiresAt: string | null;
}

export function parseSsoHealth(data: unknown): SsoHealth | null {
  const h = (data as { ssoHealth?: Record<string, unknown> } | null)?.ssoHealth;
  if (!h || typeof h !== 'object') return null;
  const status = h.status;
  if (status !== 'ok' && status !== 'expiring' && status !== 'expired' && status !== 'unknown') return null;
  const exp = h.secretExpiresAt as { toDate?: () => Date } | string | null | undefined;
  const secretExpiresAt = typeof exp === 'string' ? exp : exp && typeof exp.toDate === 'function' ? exp.toDate().toISOString() : null;
  return { status, daysLeft: typeof h.daysLeft === 'number' ? h.daysLeft : null, secretExpiresAt };
}

export function useOrgHealth(orgId: string | null) {
  return useQuery({
    queryKey: ['org-health', orgId],
    enabled: Boolean(orgId),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const snap = await getDoc(doc(getFirestore(app), 'orgs', orgId!));
      return parseSsoHealth(snap.exists() ? snap.data() : null);
    },
  });
}
